import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, X, Check, Trash2, KeyRound, Lock, Pencil, Mail, Users, Car, GraduationCap, Info, ShieldOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import InviteUserModal from './InviteUserModal';
import InviteLinkModal from './InviteLinkModal';
import UserDeleteModal from './UserDeleteModal';
import { useParadigm } from '../../contexts/ParadigmContext';

// Shared presentation classes (theme tokens from src/index.css; see HotelStayCards / TimelineCardKit)
const btnSecondary = 'inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg border border-ui-control bg-ui-surface text-[13px] font-medium text-ui-ink-2 hover:bg-ui-hover disabled:opacity-50 disabled:cursor-not-allowed';
const btnPrimary = 'inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed';
const fieldCls = 'h-9 rounded-lg border border-ui-control bg-ui-surface text-ui-ink text-[13px] px-2.5 focus:outline-none focus:ring-2 focus:ring-ui-primary/40 disabled:bg-ui-sunk disabled:text-ui-muted disabled:cursor-not-allowed';
const pillCls = 'inline-flex items-center gap-1.5 h-6 px-[9px] rounded-full text-xs font-semibold whitespace-nowrap';
const thCls = 'px-4 py-2.5 text-left text-xs font-semibold text-ui-muted bg-ui-sunk border-b border-ui-divider whitespace-nowrap';
const tdCls = 'px-4 py-3 border-b border-ui-divider text-[13px] text-ui-ink-2 align-middle';
const iconBtn = 'inline-flex items-center justify-center w-9 h-9 rounded-lg text-ui-muted hover:bg-ui-hover hover:text-ui-ink transition-colors disabled:opacity-50';
const spinnerCls = 'animate-spin rounded-full h-8 w-8 border-2 border-ui-line border-t-ui-primary';

const AVATAR_TONES = [
  'bg-ui-blue-bg text-ui-blue-fg',
  'bg-ui-teal-bg text-ui-teal-fg',
  'bg-ui-violet-bg text-ui-violet-fg',
  'bg-ui-green-bg text-ui-green-fg',
  'bg-ui-amber-bg text-ui-amber-fg',
  'bg-ui-pink-bg text-ui-pink-fg',
  'bg-ui-indigo-bg text-ui-indigo-fg',
  'bg-ui-orange-bg text-ui-orange-fg'
];

const avatarTone = (seed) => {
  const str = String(seed || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

const Avatar = ({ first, last, email, seed }) => {
  const initials = `${(first || '').trim().charAt(0)}${(last || '').trim().charAt(0)}`.toUpperCase()
    || (email || '?').charAt(0).toUpperCase();
  return (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-none ${avatarTone(seed || email)}`}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};

const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'users';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [showUrbanrideModal, setShowUrbanrideModal] = useState(false);
  const [urbanrideUser, setUrbanrideUser] = useState(null);
  const [rbacStatus, setRbacStatus] = useState(null);
  const [executiveProfiles, setExecutiveProfiles] = useState([]);
  const { user, actingOrganization } = useAuth();

  const isSuperadmin = user?.role === 'superadmin';
  const isOrgAdmin = user?.is_organization_admin;

  // Helper to check RBAC permission
  const hasRbacPermission = (permission) => {
    if (!rbacStatus?.rbac_enabled) return false;
    const userPerms = rbacStatus?.user_permissions || [];
    if (userPerms.includes('org_admin')) return true;
    return userPerms.includes(permission);
  };

  // Check if user has access via RBAC
  const hasRbacAccess = hasRbacPermission('users_invites');
  const hasAccess = isSuperadmin || isOrgAdmin || hasRbacAccess;

  // Fetch RBAC status on mount
  useEffect(() => {
    const fetchRbacStatus = async () => {
      try {
        const res = await axios.get(`${getApiBase()}/rbac/status`, { headers: getAuthHeaders() });
        setRbacStatus(res.data);
      } catch (err) {
        // RBAC may not be enabled
        console.log('RBAC status not available');
      }
    };
    fetchRbacStatus();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [user, activeTab, hasAccess]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const requests = [
        axios.get(`${apiBase}/admin/users`, { headers }),
        axios.get(`${apiBase}/admin/invites`, { headers })
      ];

      // Only superadmins need the organizations list for the invite modal
      if (isSuperadmin) {
        requests.push(axios.get(`${apiBase}/admin/organizations`, { headers }));
      }

      const responses = await Promise.all(requests);
      const [usersResponse, invitesResponse, orgsResponse] = responses;

      setUsers(usersResponse.data);
      setInvites(invitesResponse.data);

      if (orgsResponse) {
        setOrganizations(orgsResponse.data);
      } else if (user?.organization_id) {
        // For org admins, create a single-item list with their org
        setOrganizations([{
          id: user.organization_id,
          name: user.organization_name || 'Your Organization'
        }]);
      }

      // Fetch executive profiles for linking executive users
      try {
        const execResponse = await axios.get(`${apiBase}/executives`, { headers });
        setExecutiveProfiles(Array.isArray(execResponse.data) ? execResponse.data : []);
      } catch (execErr) {
        console.log('Could not fetch executives:', execErr.message);
        setExecutiveProfiles([]);
      }
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (inviteData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // For org admins, ensure the organization_id is their own
      const finalInviteData = isOrgAdmin && !isSuperadmin
        ? { ...inviteData, organization_id: user.organization_id }
        : inviteData;

      const response = await axios.post(`${apiBase}/admin/invite-user`, finalInviteData, { headers });
      setShowInviteUserModal(false);

      if (response.data.status === 'added_to_org') {
        // Existing user was added to the organization as a multi-org member
        setError('');
        const orgName = response.data.organization_name || 'the organization';
        alert(`${response.data.email} has been added to ${orgName}. They can now switch to this organization from their dashboard.`);
        loadData();
      } else if (response.data.status === 'already_member') {
        // User is already a member — show info message
        setError('');
        const orgName = response.data.organization_name || 'the organization';
        alert(`${response.data.email} is already a member of ${orgName}.`);
        loadData();
      } else if (response.data.status === 'approved') {
        // New user invite — show invite link modal
        setError('');

        // Find organization name
        const organization = organizations.find(org => org.id === finalInviteData.organization_id);
        const organizationName = organization ? organization.name : 'the organization';

        setInviteResult({
          inviteLink: response.data.invite_link,
          inviteToken: response.data.invite_token,
          email: inviteData.email,
          organizationName: organizationName,
          role: inviteData.role,
          emailSent: response.data.email_sent || false
        });

        setShowInviteLinkModal(true);
        loadData();
      }
    } catch (err) {
      console.error('Failed to invite user:', err);
      setError('Failed to invite user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleApproveInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/approve-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to approve invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRejectInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/reject-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to reject invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRemoveUser = async (userId) => {
    const userToRemove = users.find(u => u.id === userId);
    if (!userToRemove) {
      setError('User not found');
      return;
    }

    // Prevent org admins from removing superadmins
    if (!isSuperadmin && userToRemove.role === 'superadmin') {
      setError('You cannot remove a superadmin user');
      return;
    }

    setUserToDelete(userToRemove);
    setShowUserDeleteModal(true);
  };

  const confirmUserDelete = async () => {
    if (!userToDelete) return;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/users/${userToDelete.id}`, { headers });

      const updatedUsers = users.filter(u => u.id !== userToDelete.id);
      setUsers(updatedUsers);
      setError('');

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-ui-green-bg border border-ui-green-line text-ui-green-fg px-4 py-3 rounded-lg shadow-ui-pop z-50';
      successMessage.textContent = `User ${userToDelete.first_name} ${userToDelete.last_name} removed successfully`;
      document.body.appendChild(successMessage);

      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);
    } catch (error) {
      console.error('Delete user error:', error);
      let errorMessage = 'Failed to remove user';

      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You cannot remove this user.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found or already removed.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      setError(errorMessage);
    } finally {
      setShowUserDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const cancelUserDelete = () => {
    setShowUserDeleteModal(false);
    setUserToDelete(null);
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/role`, { role: newRole }, { headers });
      loadData();
    } catch (err) {
      setError('Failed to update user role: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleLinkExecutive = async (userId, executiveId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/users/${userId}/link-executive`,
        { executive_id: executiveId || null },
        { headers }
      );
      loadData();
    } catch (err) {
      setError('Failed to link executive profile: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateOrgAdmin = async (userId, isOrgAdminStatus) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/organization-admin`,
        { is_organization_admin: isOrgAdminStatus },
        { headers }
      );
      loadData();
    } catch (err) {
      setError('Failed to update organization admin status: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUrbanrideSettings = (userItem) => {
    setUrbanrideUser(userItem);
    setShowUrbanrideModal(true);
  };

  // Access check - superadmins, org admins, and RBAC users_invites can access
  if (!hasAccess) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-ui-red-bg text-ui-red-fg flex items-center justify-center">
          <ShieldOff className="w-7 h-7" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-semibold text-ui-ink mb-2">Access Denied</h2>
        <p className="text-ui-muted">You need admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={spinnerCls} role="status" aria-label="Loading"></div>
      </div>
    );
  }

  const orgDisplayName = actingOrganization?.name || user?.organization_name || 'your organization';
  const tabs = [
    { id: 'users', label: 'Users', count: users.length, countStyle: 'muted' },
    { id: 'invites', label: 'Invites', count: invites.length, countStyle: 'amber' },
    { id: 'settings', label: isSuperadmin ? 'Urbanride Settings' : 'Settings' }
  ];

  return (
    <div className="max-w-[1120px] flex flex-col gap-5">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="m-0 text-2xl font-bold tracking-[-0.01em] text-ui-ink">Users &amp; invites</h1>
          <p className="mt-1 text-sm text-ui-muted">
            {isSuperadmin
              ? 'Manage users, invites, and Urbanride account linking'
              : `People who can sign in to ${orgDisplayName}, and what they can do.`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowInviteUserModal(true)}
            className={btnPrimary}
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            Invite user
          </button>
        </div>
      </header>

      {/* Error Message */}
      {error && (
        <div role="alert" className="flex items-start justify-between gap-3 px-4 py-3 rounded-xl bg-ui-red-bg border border-ui-red-line text-ui-red-fg text-sm">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError('')}
            className="shrink-0 rounded-md p-0.5 hover:bg-ui-hover"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      )}

      <section className="bg-ui-surface border border-ui-line rounded-2xl shadow-ui-card overflow-hidden">
        {/* Tab Navigation */}
        <div role="tablist" aria-label="Users and invites" className="flex gap-6 px-4 border-b border-ui-divider overflow-x-auto">
          {tabs.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`admin-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`admin-panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`h-11 inline-flex items-center gap-2 px-1 -mb-px border-b-2 text-sm whitespace-nowrap transition-colors ${
                  selected
                    ? 'border-ui-primary text-ui-ink font-semibold'
                    : 'border-transparent text-ui-ink-3 hover:text-ui-ink'
                }`}
              >
                {tab.label}
                {typeof tab.count === 'number' && (
                  tab.countStyle === 'amber' ? (
                    tab.count > 0 && (
                      <span className={`${pillCls} h-5 px-[7px] bg-ui-amber-bg text-ui-amber-fg`}>{tab.count}</span>
                    )
                  ) : (
                    <span className="text-xs font-medium text-ui-muted">{tab.count}</span>
                  )
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div role="tabpanel" id={`admin-panel-${activeTab}`} aria-labelledby={`admin-tab-${activeTab}`}>
          {activeTab === 'users' && (
            <UsersTab
              users={users}
              organizations={organizations}
              isSuperadmin={isSuperadmin}
              currentUser={user}
              onInviteUser={() => setShowInviteUserModal(true)}
              onRemoveUser={handleRemoveUser}
              onUpdateUserRole={handleUpdateUserRole}
              onUpdateOrgAdmin={handleUpdateOrgAdmin}
              onUrbanrideSettings={handleUrbanrideSettings}
              executiveProfiles={executiveProfiles}
              onLinkExecutive={handleLinkExecutive}
            />
          )}

          {activeTab === 'invites' && (
            <InvitesTab
              invites={invites}
              onApproveInvite={handleApproveInvite}
              onRejectInvite={handleRejectInvite}
            />
          )}

          {activeTab === 'settings' && (
            <SettingsTab isSuperadmin={isSuperadmin} />
          )}
        </div>
      </section>

      {/* Modals */}
      {showInviteUserModal && (
        <InviteUserModal
          organizations={organizations}
          onClose={() => setShowInviteUserModal(false)}
          onInvite={handleInviteUser}
          isSuperadmin={isSuperadmin}
          currentUserOrgId={user?.organization_id}
        />
      )}

      {showInviteLinkModal && inviteResult && (
        <InviteLinkModal
          inviteLink={inviteResult.inviteLink}
          inviteToken={inviteResult.inviteToken}
          email={inviteResult.email}
          organizationName={inviteResult.organizationName}
          role={inviteResult.role}
          emailSentAutomatically={inviteResult.emailSent}
          onClose={() => {
            setShowInviteLinkModal(false);
            setInviteResult(null);
          }}
          onSendEmail={null}
        />
      )}

      {showUserDeleteModal && userToDelete && (
        <UserDeleteModal
          user={userToDelete}
          onConfirm={confirmUserDelete}
          onCancel={cancelUserDelete}
        />
      )}

      {showUrbanrideModal && urbanrideUser && (
        <UrbanrideSettingsModal
          user={urbanrideUser}
          onClose={() => {
            setShowUrbanrideModal(false);
            setUrbanrideUser(null);
          }}
        />
      )}
    </div>
  );
};

// Urbanride Settings Modal Component (Superadmin only)
const UrbanrideSettingsModal = ({ user, onClose }) => {
  const [settings, setSettings] = useState({
    test_api_key_configured: false,
    test_api_key_hint: null,
    production_api_key_configured: false,
    production_api_key_hint: null,
    mode: null  // null means inherit from org
  });
  const [testApiKey, setTestApiKey] = useState('');
  const [productionApiKey, setProductionApiKey] = useState('');
  const [modeOverride, setModeOverride] = useState('inherit');  // 'inherit', 'test', or 'production'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKeys, setShowApiKeys] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [user.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/users/${user.id}/urbanride-settings`, { headers });
      setSettings(res.data);
      // Set mode override: if mode is set, use it; otherwise 'inherit'
      setModeOverride(res.data.mode || 'inherit');
    } catch (e) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      // Send mode: '' to clear override (inherit from org), or 'test'/'production' to set override
      const payload = { mode: modeOverride === 'inherit' ? '' : modeOverride };
      if (testApiKey) payload.test_api_key = testApiKey;
      if (productionApiKey) payload.production_api_key = productionApiKey;

      const res = await axios.put(`${apiBase}/admin/users/${user.id}/urbanride-settings`, payload, { headers });
      setSettings(res.data);
      setModeOverride(res.data.mode || 'inherit');
      setSuccess('Settings saved successfully');
      setTestApiKey('');
      setProductionApiKey('');
      setShowApiKeys(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClearKey = async (keyType) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const payload = keyType === 'test' ? { test_api_key: '' } : { production_api_key: '' };
      const res = await axios.put(`${apiBase}/admin/users/${user.id}/urbanride-settings`, payload, { headers });
      setSettings(res.data);
      setSuccess(`${keyType === 'test' ? 'Test' : 'Production'} API key cleared`);
    } catch (e) {
      setError('Failed to clear API key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-overlay/50 flex items-center justify-center z-[70]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="urbanride-keys-title"
        className="bg-ui-surface border border-ui-line rounded-2xl shadow-ui-pop w-full max-w-lg mx-4"
      >
        <div className="px-6 py-4 border-b border-ui-divider">
          <h3 id="urbanride-keys-title" className="text-lg font-semibold text-ui-ink">
            Urbanride API Keys - {user.first_name} {user.last_name}
          </h3>
          <p className="text-sm text-ui-muted">{user.email}</p>
        </div>

        {loading ? (
          <div className="p-6 flex justify-center">
            <div className={spinnerCls} role="status" aria-label="Loading"></div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Mode Override */}
            <fieldset>
              <legend className="text-sm font-semibold text-ui-ink mb-2">API Mode Override</legend>
              <p className="text-xs text-ui-muted mb-2">
                By default, users inherit the mode setting from their organization. Set an override to use a different mode for this specific user.
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm text-ui-ink-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urbanrideMode"
                    value="inherit"
                    checked={modeOverride === 'inherit'}
                    onChange={() => setModeOverride('inherit')}
                    className="w-4 h-4 accent-[rgb(var(--ui-primary))]"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-ui-faint rounded-full" aria-hidden="true"></span>
                    Inherit from Organization
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urbanrideMode"
                    value="test"
                    checked={modeOverride === 'test'}
                    onChange={() => setModeOverride('test')}
                    className="w-4 h-4 accent-[rgb(var(--ui-primary))]"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-ui-amber-solid rounded-full" aria-hidden="true"></span>
                    Test Mode
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="urbanrideMode"
                    value="production"
                    checked={modeOverride === 'production'}
                    onChange={() => setModeOverride('production')}
                    className="w-4 h-4 accent-[rgb(var(--ui-primary))]"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-ui-green-solid rounded-full" aria-hidden="true"></span>
                    Production Mode
                  </span>
                </label>
              </div>
              <p className="text-xs text-ui-muted mt-2">
                {modeOverride === 'inherit'
                  ? 'User will use their organization\'s mode setting'
                  : modeOverride === 'test'
                    ? 'Override: Using test.urbanride.com - no real bookings'
                    : 'Override: Using apps.urbanride.com - LIVE bookings'
                }
              </p>
            </fieldset>

            {/* API Key Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-ui-sunk border border-ui-divider rounded-xl">
                <div className="text-[13px] font-semibold text-ui-ink-2 mb-1">Test API Key</div>
                {settings.test_api_key_configured ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ui-green-fg">****{settings.test_api_key_hint}</span>
                    <button
                      type="button"
                      onClick={() => handleClearKey('test')}
                      disabled={saving}
                      className="text-xs font-medium text-ui-red-fg hover:underline disabled:opacity-50"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-ui-muted">Not configured</span>
                )}
              </div>
              <div className="p-3 bg-ui-sunk border border-ui-divider rounded-xl">
                <div className="text-[13px] font-semibold text-ui-ink-2 mb-1">Production API Key</div>
                {settings.production_api_key_configured ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ui-green-fg">****{settings.production_api_key_hint}</span>
                    <button
                      type="button"
                      onClick={() => handleClearKey('production')}
                      disabled={saving}
                      className="text-xs font-medium text-ui-red-fg hover:underline disabled:opacity-50"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-ui-muted">Not configured</span>
                )}
              </div>
            </div>

            {/* Toggle to show/hide API key inputs */}
            <button
              type="button"
              onClick={() => setShowApiKeys(!showApiKeys)}
              aria-expanded={showApiKeys}
              className="text-sm font-medium text-ui-link hover:underline"
            >
              {showApiKeys ? 'Hide API Key Fields' : 'Update API Keys'}
            </button>

            {/* API Key Inputs */}
            {showApiKeys && (
              <div className="space-y-3 p-4 bg-ui-sunk border border-ui-divider rounded-xl">
                <div>
                  <label htmlFor="urbanride-test-key" className="block text-[13px] font-semibold text-ui-ink-2 mb-1">Test API Key</label>
                  <input
                    id="urbanride-test-key"
                    type="password"
                    value={testApiKey}
                    onChange={(e) => setTestApiKey(e.target.value)}
                    placeholder={settings.test_api_key_configured ? 'Enter new key to replace' : 'Enter test API key'}
                    className={`${fieldCls} w-full h-10 px-3 placeholder:text-ui-faint`}
                  />
                </div>
                <div>
                  <label htmlFor="urbanride-production-key" className="block text-[13px] font-semibold text-ui-ink-2 mb-1">Production API Key</label>
                  <input
                    id="urbanride-production-key"
                    type="password"
                    value={productionApiKey}
                    onChange={(e) => setProductionApiKey(e.target.value)}
                    placeholder={settings.production_api_key_configured ? 'Enter new key to replace' : 'Enter production API key'}
                    className={`${fieldCls} w-full h-10 px-3 placeholder:text-ui-faint`}
                  />
                </div>
              </div>
            )}

            {error && <div role="alert" className="text-ui-red-fg text-sm">{error}</div>}
            {success && <div role="status" className="text-ui-green-fg text-sm">{success}</div>}
          </div>
        )}

        <div className="px-6 py-4 border-t border-ui-divider flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className={`${btnSecondary} h-10 px-4`}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className={btnPrimary}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Users Tab Component
// Note: onUrbanrideSettings is only available for superadmins.
// The "Invite user" action (onInviteUser) lives in the page header.
const UsersTab = ({ users, organizations, isSuperadmin, currentUser, onInviteUser, onRemoveUser, onUpdateUserRole, onUpdateOrgAdmin, onUrbanrideSettings, executiveProfiles = [], onLinkExecutive }) => {
  const { labels, isSpeakerBureau } = useParadigm();
  const { actingOrganization } = useAuth();

  const resolveOrgName = (userItem) => {
    const primaryName = organizations.find(org => org.id === userItem.organization_id)?.name || userItem.organization_id;
    // For multi-org members viewing via acting org, show the acting org name
    const actOrgId = actingOrganization?.id;
    const isMultiOrg = actOrgId && userItem.organization_id !== actOrgId && (userItem.additional_organization_ids || []).includes(actOrgId);
    return isMultiOrg ? (actingOrganization?.name || primaryName) : primaryName;
  };

  const renderNameBlock = (userItem, fullName, isSelf, isSuperadminUser) => (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar first={userItem.first_name} last={userItem.last_name} email={userItem.email} seed={userItem.id} />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap font-semibold text-ui-ink">
          <span className="truncate">{fullName}</span>
          {isSelf && (
            <span className={`${pillCls} h-5 px-[7px] bg-ui-hover text-ui-ink-3`}>You</span>
          )}
          {isSuperadminUser && (
            <span className={`${pillCls} h-5 px-[7px] bg-ui-violet-bg text-ui-violet-fg`}>Superadmin</span>
          )}
        </div>
        <div className="text-xs text-ui-muted truncate">{userItem.email}</div>
      </div>
    </div>
  );

  const renderRoleControls = (userItem, fullName, canModify, isSuperadminUser, isSpeakerPortalUser, compact) => (
    <div className={compact ? 'flex flex-col gap-2' : 'flex flex-col sm:flex-row sm:flex-wrap gap-2'}>
      {isSpeakerPortalUser ? (
        <span
          className={`${pillCls} self-start bg-ui-pink-bg text-ui-pink-fg`}
          title="Speaker portal accounts are view-only; manage them from the Speakers page"
        >
          Speaker (portal)
        </span>
      ) : (
        <label className="block">
          <span className="sr-only">Role for {fullName}</span>
          <select
            value={userItem.role}
            onChange={(e) => onUpdateUserRole(userItem.id, e.target.value)}
            disabled={!canModify || isSuperadminUser}
            className={`${fieldCls} ${compact ? 'w-full max-w-[160px]' : 'w-full'}`}
          >
            <option value="ea">{labels.ea}</option>
            {(!isSpeakerBureau || userItem.role === 'executive') && (
              <option value="executive">{labels.executive}</option>
            )}
            <option value="other">Other</option>
            {isSuperadminUser && <option value="superadmin">Superadmin</option>}
          </select>
        </label>
      )}

      {!isSuperadminUser && !isSpeakerPortalUser && (
        <label className={`flex items-center gap-2 text-xs text-ui-ink-3 ${!canModify ? 'cursor-not-allowed' : 'cursor-pointer'} ${compact ? '' : 'sm:px-1'}`}>
          <input
            type="checkbox"
            checked={userItem.is_organization_admin || false}
            onChange={(e) => onUpdateOrgAdmin(userItem.id, e.target.checked)}
            disabled={!canModify}
            className={`h-3.5 w-3.5 rounded border-ui-control accent-[rgb(var(--ui-primary))] ${
              !canModify ? 'cursor-not-allowed' : ''
            }`}
          />
          Org Admin
        </label>
      )}

      {userItem.role === 'executive' && executiveProfiles.length > 0 && (
        <label className="block">
          <span className="sr-only">Executive profile for {fullName}</span>
          <select
            value={userItem.linked_executive_id || ''}
            onChange={(e) => onLinkExecutive(userItem.id, e.target.value)}
            disabled={!canModify}
            className={`${fieldCls} ${compact ? 'w-full max-w-[200px] text-xs' : 'w-full'}`}
          >
            <option value="">{compact ? 'Link to Executive...' : 'Link to Executive Profile...'}</option>
            {executiveProfiles.map((exec) => (
              <option key={exec.id} value={exec.id}>
                {exec.first_name} {exec.last_name}{!compact && exec.email ? ` (${exec.email})` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );

  const renderActions = (userItem, fullName, canModify, isSuperadminUser, isSpeakerPortalUser, showLocked) => (
    <div className="flex items-center justify-end gap-1">
      {/* Urbanride settings - superadmin only; speakers never book rides */}
      {isSuperadmin && !isSpeakerPortalUser && (
        <button
          type="button"
          onClick={() => onUrbanrideSettings(userItem)}
          className={iconBtn}
          title="Urbanride API Keys"
          aria-label={`Urbanride API keys for ${fullName}`}
        >
          <KeyRound className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
      {canModify && !isSuperadminUser ? (
        <button
          type="button"
          onClick={() => onRemoveUser(userItem.id)}
          className={`${iconBtn} hover:bg-ui-red-bg hover:text-ui-red-fg`}
          title="Remove User"
          aria-label={`Remove ${fullName}`}
        >
          <Trash2 className="w-4 h-4" aria-hidden="true" />
        </button>
      ) : showLocked ? (
        <span className="inline-flex items-center justify-center w-9 h-9 text-ui-faint" title="Cannot modify">
          <Lock className="w-4 h-4" aria-hidden="true" />
          <span className="sr-only">Cannot modify {fullName}</span>
        </span>
      ) : null}
    </div>
  );

  if (users.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <Users className="w-10 h-10 mx-auto mb-3 text-ui-faint" aria-hidden="true" />
        <p className="text-sm text-ui-muted">No users yet</p>
      </div>
    );
  }

  return (
  <div>
    {/* Mobile Card View */}
    <ul className="block md:hidden">
      {users.map((userItem) => {
        const isSuperadminUser = userItem.role === 'superadmin';
        const isSpeakerPortalUser = userItem.role === 'speaker';
        const canModify = isSuperadmin || (!isSuperadminUser && userItem.id !== currentUser?.id);
        const isSelf = currentUser?.id != null && userItem.id === currentUser.id;
        const fullName = `${userItem.first_name || ''} ${userItem.last_name || ''}`.trim() || userItem.email;
        const orgName = resolveOrgName(userItem);

        return (
          <li key={userItem.id} className="p-4 space-y-3 border-b border-ui-divider last:border-b-0">
            {/* User Info */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                {renderNameBlock(userItem, fullName, isSelf, isSuperadminUser)}
                {isSuperadmin && (
                  <div className="text-xs text-ui-faint mt-1 ml-11 truncate">{orgName}</div>
                )}
              </div>
              {renderActions(userItem, fullName, canModify, isSuperadminUser, isSpeakerPortalUser, false)}
            </div>

            {/* Role Controls */}
            <div className="pt-3 border-t border-ui-divider">
              {renderRoleControls(userItem, fullName, canModify, isSuperadminUser, isSpeakerPortalUser, false)}
            </div>
          </li>
        );
      })}
    </ul>

    {/* Desktop Table View */}
    <div className="hidden md:block overflow-x-auto">
      <table className="min-w-full border-collapse">
        <thead>
          <tr>
            <th scope="col" className={thCls}>Name</th>
            {isSuperadmin && (
              <th scope="col" className={thCls}>Organization</th>
            )}
            <th scope="col" className={thCls}>Role</th>
            <th scope="col" className={`${thCls} w-24`}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {users.map((userItem) => {
            const isSuperadminUser = userItem.role === 'superadmin';
            const isSpeakerPortalUser = userItem.role === 'speaker';
            const canModify = isSuperadmin || (!isSuperadminUser && userItem.id !== currentUser?.id);
            const isSelf = currentUser?.id != null && userItem.id === currentUser.id;
            const fullName = `${userItem.first_name || ''} ${userItem.last_name || ''}`.trim() || userItem.email;

            return (
              <tr key={userItem.id} className="hover:bg-ui-hover">
                <td className={tdCls}>
                  {renderNameBlock(userItem, fullName, isSelf, isSuperadminUser)}
                </td>
                {isSuperadmin && (
                  <td className={tdCls}>
                    <span className="truncate block max-w-[200px]">
                      {resolveOrgName(userItem)}
                    </span>
                  </td>
                )}
                <td className={tdCls}>
                  {renderRoleControls(userItem, fullName, canModify, isSuperadminUser, isSpeakerPortalUser, true)}
                </td>
                <td className={`${tdCls} text-right`}>
                  {renderActions(userItem, fullName, canModify, isSuperadminUser, isSpeakerPortalUser, true)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);
};

// Invites Tab Component
const InvitesTab = ({ invites, onApproveInvite, onRejectInvite }) => (
  <div>
    <div className="px-4 pt-4 pb-3">
      <h2 className="text-[15px] font-semibold text-ui-ink">Pending Invites</h2>
      <p className="text-[13px] text-ui-muted">Review and approve user invitations</p>
    </div>

    {invites.length === 0 ? (
      <div className="text-center py-12 px-4 border-t border-ui-divider">
        <Mail className="w-10 h-10 mx-auto mb-3 text-ui-faint" aria-hidden="true" />
        <h3 className="text-base font-semibold text-ui-ink mb-1">No pending invites</h3>
        <p className="text-sm text-ui-muted">All invitations have been processed</p>
      </div>
    ) : (
      <>
        {/* Mobile Card View */}
        <ul className="block md:hidden border-t border-ui-divider">
          {invites.map((invite) => (
            <li key={invite.id} className="p-4 border-b border-ui-divider last:border-b-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ui-ink truncate">{invite.email}</div>
                  <div className="text-xs text-ui-muted truncate">{invite.organization_name}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`${pillCls} bg-ui-blue-bg text-ui-blue-fg`}>
                      {invite.role}
                    </span>
                    <span className="text-xs text-ui-faint">by {invite.invited_by_name}</span>
                  </div>
                </div>
                <InviteActions invite={invite} onApproveInvite={onApproveInvite} onRejectInvite={onRejectInvite} />
              </div>
            </li>
          ))}
        </ul>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th scope="col" className={thCls}>Email</th>
                <th scope="col" className={thCls}>Organization</th>
                <th scope="col" className={thCls}>Role</th>
                <th scope="col" className={thCls}>Invited By</th>
                <th scope="col" className={`${thCls} w-24`}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id} className="hover:bg-ui-hover">
                  <td className={`${tdCls} font-semibold text-ui-ink`}>
                    <span className="truncate block max-w-[240px]">{invite.email}</span>
                  </td>
                  <td className={tdCls}>
                    <span className="truncate block max-w-[180px]">{invite.organization_name}</span>
                  </td>
                  <td className={tdCls}>
                    <span className={`${pillCls} bg-ui-blue-bg text-ui-blue-fg`}>
                      {invite.role}
                    </span>
                  </td>
                  <td className={`${tdCls} text-ui-muted`}>
                    <span className="truncate block max-w-[160px]">{invite.invited_by_name}</span>
                  </td>
                  <td className={`${tdCls} text-right`}>
                    <InviteActions invite={invite} onApproveInvite={onApproveInvite} onRejectInvite={onRejectInvite} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )}
  </div>
);

const InviteActions = ({ invite, onApproveInvite, onRejectInvite }) => (
  <div className="flex items-center justify-end gap-1">
    <button
      type="button"
      onClick={() => onApproveInvite(invite.id)}
      className={`${iconBtn} hover:bg-ui-green-bg hover:text-ui-green-fg`}
      title="Approve Invite"
      aria-label={`Approve invite for ${invite.email}`}
    >
      <Check className="w-4 h-4" aria-hidden="true" />
    </button>
    <button
      type="button"
      onClick={() => onRejectInvite(invite.id)}
      className={`${iconBtn} hover:bg-ui-red-bg hover:text-ui-red-fg`}
      title="Reject Invite"
      aria-label={`Reject invite for ${invite.email}`}
    >
      <X className="w-4 h-4" aria-hidden="true" />
    </button>
  </div>
);

// Tutorial Settings Card - allows org admins to toggle tutorials for their organization
const TutorialSettingsCard = () => {
  const { user } = useAuth();
  const [tutorialEnabled, setTutorialEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const orgId = user?.organization_id;

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const res = await axios.get(`${apiBase}/organizations/${orgId}/tutorial-settings`, { headers });
        setTutorialEnabled(res.data.tutorial_enabled);
      } catch (e) {
        console.log('Could not load tutorial settings:', e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId]);

  const handleToggle = async () => {
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const newValue = !tutorialEnabled;
      await axios.put(`${apiBase}/organizations/${orgId}/tutorial-settings`, { tutorial_enabled: newValue }, { headers });
      setTutorialEnabled(newValue);
    } catch (e) {
      console.error('Failed to update tutorial settings:', e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-ui-surface border border-ui-line rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-ui-divider">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-ui-teal-bg text-ui-teal-fg rounded-lg flex items-center justify-center">
            <GraduationCap className="w-5 h-5" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-ui-ink">Quick Start Tutorial</h3>
            <p className="text-[13px] text-ui-muted">Control the guided tutorial for your organization's users</p>
          </div>
        </div>
      </div>
      <div className="px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-ui-ink">Enable Tutorial</p>
            <p className="text-[13px] text-ui-muted">
              {tutorialEnabled
                ? 'New users will see the guided tutorial on first login'
                : 'Tutorial is disabled for all users in this organization'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ui-primary/40 focus:ring-offset-2 ${
              tutorialEnabled ? 'bg-ui-primary' : 'bg-ui-control'
            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            role="switch"
            aria-checked={tutorialEnabled}
            aria-label="Toggle tutorial"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-ui-on-primary shadow ring-0 transition duration-200 ease-in-out ${
                tutorialEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

// Settings Tab Component (Superadmin only - Urbanride Account Linking)
const SettingsTab = ({ isSuperadmin }) => {
  const [organizations, setOrganizations] = useState([]);
  const [urbanrideAccounts, setUrbanrideAccounts] = useState({ test: [], production: [] });
  const [loading, setLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState({ test: false, production: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingOrg, setEditingOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [appSettings, setAppSettings] = useState({ urbanride_default_mode: 'test' });
  const [savingAppSettings, setSavingAppSettings] = useState(false);

  // Load organizations with Urbanride settings and app settings
  useEffect(() => {
    if (isSuperadmin) {
      loadOrganizations();
      loadAppSettings();
      loadUrbanrideAccounts('test');
      loadUrbanrideAccounts('production');
    }
  }, [isSuperadmin]);

  const loadAppSettings = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/app-settings`, { headers });
      setAppSettings(res.data);
    } catch (e) {
      console.log('Could not load app settings:', e?.response?.data?.detail || e.message);
    }
  };

  const handleAppSettingsChange = async (newDefaultMode) => {
    setSavingAppSettings(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/app-settings`, { urbanride_default_mode: newDefaultMode }, { headers });
      setAppSettings({ ...appSettings, urbanride_default_mode: newDefaultMode });
      setSuccess(`Default mode for new organizations set to ${newDefaultMode}`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save app settings');
    } finally {
      setSavingAppSettings(false);
    }
  };

  const handleOrgModeChange = async (orgId, newMode) => {
    setSaving(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/organizations/${orgId}/urbanride-settings`, { mode: newMode }, { headers });

      // Update local state
      setOrganizations(orgs => orgs.map(org =>
        org.organization_id === orgId ? { ...org, mode: newMode } : org
      ));

      setSuccess(`Organization mode updated to ${newMode}`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to update organization mode');
    } finally {
      setSaving(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/urbanride/organizations`, { headers });
      setOrganizations(res.data.organizations || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const loadUrbanrideAccounts = async (mode) => {
    try {
      setAccountsLoading(prev => ({ ...prev, [mode]: true }));
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/admin/urbanride/accounts?mode=${mode}`, { headers });
      setUrbanrideAccounts(prev => ({ ...prev, [mode]: res.data.accounts || [] }));
    } catch (e) {
      // Don't show error - Bot API may not be configured
      console.log(`Could not load ${mode} Urbanride accounts:`, e?.response?.data?.detail || e.message);
    } finally {
      setAccountsLoading(prev => ({ ...prev, [mode]: false }));
    }
  };

  const handleEditOrg = (org) => {
    setEditingOrg({
      ...org,
      account_id_test: org.account_id_test || '',
      account_id_production: org.account_id_production || '',
      auto_provision_enabled: org.auto_provision_enabled !== false
    });
  };

  const handleSave = async () => {
    if (!editingOrg) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Find account names for selected IDs
      const testAccount = urbanrideAccounts.test.find(a => a.id === parseInt(editingOrg.account_id_test));
      const prodAccount = urbanrideAccounts.production.find(a => a.id === parseInt(editingOrg.account_id_production));

      const payload = {
        account_id_test: editingOrg.account_id_test ? parseInt(editingOrg.account_id_test) : null,
        account_name_test: testAccount?.name || null,
        account_id_production: editingOrg.account_id_production ? parseInt(editingOrg.account_id_production) : null,
        account_name_production: prodAccount?.name || null,
        auto_provision_enabled: editingOrg.auto_provision_enabled
      };

      await axios.put(`${apiBase}/admin/organizations/${editingOrg.organization_id}/urbanride-settings`, payload, { headers });

      // Update local state
      setOrganizations(orgs => orgs.map(org =>
        org.organization_id === editingOrg.organization_id
          ? {
              ...org,
              account_id_test: payload.account_id_test,
              account_name_test: payload.account_name_test,
              account_id_production: payload.account_id_production,
              account_name_production: payload.account_name_production,
              auto_provision_enabled: payload.auto_provision_enabled
            }
          : org
      ));

      setSuccess(`Urbanride settings updated for ${editingOrg.organization_name}`);
      setEditingOrg(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAccount = async (orgId, mode) => {
    setSaving(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const payload = mode === 'test'
        ? { account_id_test: null, account_name_test: null }
        : { account_id_production: null, account_name_production: null };

      await axios.put(`${apiBase}/admin/organizations/${orgId}/urbanride-settings`, payload, { headers });

      // Update local state
      setOrganizations(orgs => orgs.map(org =>
        org.organization_id === orgId
          ? { ...org, ...payload }
          : org
      ));

      setSuccess(`${mode === 'test' ? 'Test' : 'Production'} account unlinked`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to clear account');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="p-4 sm:p-5 space-y-5">
        <TutorialSettingsCard />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 space-y-5">
      <TutorialSettingsCard />

      {/* Header */}
      <div className="bg-ui-surface border border-ui-line rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-ui-divider">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-ui-indigo-bg text-ui-indigo-fg rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-ui-ink">Urbanride Account Linking</h3>
              <p className="text-[13px] text-ui-muted">Link organizations with Urbanride accounts for automatic API key provisioning</p>
            </div>
          </div>
        </div>

        <div className="p-5">
          {/* Status Messages */}
          {error && (
            <div role="alert" className="mb-4 px-4 py-3 bg-ui-red-bg border border-ui-red-line text-ui-red-fg text-sm rounded-xl flex justify-between items-center gap-3">
              <span>{error}</span>
              <button type="button" onClick={() => setError('')} className="rounded-md p-0.5 hover:bg-ui-hover" aria-label="Dismiss error">
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}
          {success && (
            <div role="status" className="mb-4 px-4 py-3 bg-ui-green-bg border border-ui-green-line text-ui-green-fg text-sm rounded-xl flex justify-between items-center gap-3">
              <span>{success}</span>
              <button type="button" onClick={() => setSuccess('')} className="rounded-md p-0.5 hover:bg-ui-hover" aria-label="Dismiss message">
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* App-Level Default Mode Setting */}
          <div className="mb-5 p-4 bg-ui-violet-bg border border-ui-violet-line rounded-xl">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-ui-violet-fg">Default Mode for New Organizations</h4>
                <p className="text-[13px] text-ui-violet-fg/80">This setting applies when new organizations are created</p>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="urbanride-default-mode" className="sr-only">Default mode for new organizations</label>
                <select
                  id="urbanride-default-mode"
                  value={appSettings.urbanride_default_mode || 'test'}
                  onChange={(e) => handleAppSettingsChange(e.target.value)}
                  disabled={savingAppSettings}
                  className={`${fieldCls} h-10 px-3`}
                >
                  <option value="test">Test Mode</option>
                  <option value="production">Production Mode</option>
                </select>
                {savingAppSettings && (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-ui-violet-line border-t-ui-violet-fg" role="status" aria-label="Saving"></div>
                )}
              </div>
            </div>
          </div>

          {/* Information Box */}
          <div className="mb-5 p-4 bg-ui-blue-bg border border-ui-blue-line rounded-xl text-ui-blue-fg">
            <h4 className="flex items-center gap-2 text-sm font-semibold mb-2">
              <Info className="w-4 h-4" aria-hidden="true" />
              How it works
            </h4>
            <ul className="text-[13px] space-y-1 list-disc list-inside">
              <li>Link each organization to an Urbanride account</li>
              <li>When users first try to book, their API key will be automatically created</li>
              <li>Users are matched by email address to existing Urbanride bookers</li>
              <li>New bookers are created automatically for users without existing profiles</li>
            </ul>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className={spinnerCls} role="status" aria-label="Loading"></div>
            </div>
          ) : (
            <div className="overflow-x-auto border border-ui-line rounded-xl">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th scope="col" className={thCls}>Organization</th>
                    <th scope="col" className={thCls}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-ui-amber-solid rounded-full" aria-hidden="true"></span>
                        Test Account
                      </span>
                    </th>
                    <th scope="col" className={thCls}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-ui-green-solid rounded-full" aria-hidden="true"></span>
                        Production Account
                      </span>
                    </th>
                    <th scope="col" className={`${thCls} text-center`}>Auto-Provision</th>
                    <th scope="col" className={`${thCls} text-center`}>Mode</th>
                    <th scope="col" className={`${thCls} text-right`}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org) => (
                    <tr key={org.organization_id} className="hover:bg-ui-hover">
                      <td className={`${tdCls} font-semibold text-ui-ink`}>{org.organization_name}</td>
                      <td className={tdCls}>
                        {org.account_id_test ? (
                          <div className="flex items-center gap-1">
                            <span className="text-ui-ink">{org.account_name_test || `ID: ${org.account_id_test}`}</span>
                            <button
                              type="button"
                              onClick={() => handleClearAccount(org.organization_id, 'test')}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-ui-muted hover:bg-ui-red-bg hover:text-ui-red-fg disabled:opacity-50"
                              disabled={saving}
                              aria-label={`Unlink test account for ${org.organization_name}`}
                            >
                              <X className="w-3.5 h-3.5" aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-ui-faint">Not linked</span>
                        )}
                      </td>
                      <td className={tdCls}>
                        {org.account_id_production ? (
                          <div className="flex items-center gap-1">
                            <span className="text-ui-ink">{org.account_name_production || `ID: ${org.account_id_production}`}</span>
                            <button
                              type="button"
                              onClick={() => handleClearAccount(org.organization_id, 'production')}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-ui-muted hover:bg-ui-red-bg hover:text-ui-red-fg disabled:opacity-50"
                              disabled={saving}
                              aria-label={`Unlink production account for ${org.organization_name}`}
                            >
                              <X className="w-3.5 h-3.5" aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-ui-faint">Not linked</span>
                        )}
                      </td>
                      <td className={`${tdCls} text-center`}>
                        <span className={`${pillCls} ${
                          org.auto_provision_enabled !== false ? 'bg-ui-green-bg text-ui-green-fg' : 'bg-ui-hover text-ui-ink-3'
                        }`}>
                          {org.auto_provision_enabled !== false ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className={`${tdCls} text-center`}>
                        <label>
                          <span className="sr-only">Mode for {org.organization_name}</span>
                          <select
                            value={org.mode || 'test'}
                            onChange={(e) => handleOrgModeChange(org.organization_id, e.target.value)}
                            disabled={saving}
                            className={`${fieldCls} font-medium ${
                              (org.mode || 'test') === 'production'
                                ? 'border-ui-green-line bg-ui-green-bg text-ui-green-fg'
                                : 'border-ui-amber-line bg-ui-amber-bg text-ui-amber-fg'
                            }`}
                          >
                            <option value="test">Test</option>
                            <option value="production">Production</option>
                          </select>
                        </label>
                      </td>
                      <td className={`${tdCls} text-right`}>
                        <button
                          type="button"
                          onClick={() => handleEditOrg(org)}
                          className={iconBtn}
                          title="Edit"
                          aria-label={`Edit Urbanride accounts for ${org.organization_name}`}
                        >
                          <Pencil className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingOrg && (
        <div className="fixed inset-0 bg-ui-overlay/50 flex items-center justify-center z-[70]">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="link-urbanride-title"
            className="bg-ui-surface border border-ui-line rounded-2xl shadow-ui-pop w-full max-w-lg mx-4"
          >
            <div className="px-6 py-4 border-b border-ui-divider">
              <h3 id="link-urbanride-title" className="text-lg font-semibold text-ui-ink">
                Link Urbanride Accounts - {editingOrg.organization_name}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Test Account Selection */}
              <div>
                <label htmlFor="link-test-account" className="block text-[13px] font-semibold text-ui-ink-2 mb-2">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-ui-amber-solid rounded-full" aria-hidden="true"></span>
                    Test Environment Account
                  </span>
                </label>
                <select
                  id="link-test-account"
                  value={editingOrg.account_id_test || ''}
                  onChange={(e) => setEditingOrg({ ...editingOrg, account_id_test: e.target.value })}
                  className={`${fieldCls} w-full h-10 px-3`}
                  disabled={accountsLoading.test}
                >
                  <option value="">-- Not linked --</option>
                  {urbanrideAccounts.test.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} (ID: {account.id})
                    </option>
                  ))}
                </select>
                {urbanrideAccounts.test.length === 0 && !accountsLoading.test && (
                  <p className="mt-1 text-xs text-ui-amber-fg">
                    No accounts available. Check Bot API configuration.
                  </p>
                )}
              </div>

              {/* Production Account Selection */}
              <div>
                <label htmlFor="link-production-account" className="block text-[13px] font-semibold text-ui-ink-2 mb-2">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-ui-green-solid rounded-full" aria-hidden="true"></span>
                    Production Environment Account
                  </span>
                </label>
                <select
                  id="link-production-account"
                  value={editingOrg.account_id_production || ''}
                  onChange={(e) => setEditingOrg({ ...editingOrg, account_id_production: e.target.value })}
                  className={`${fieldCls} w-full h-10 px-3`}
                  disabled={accountsLoading.production}
                >
                  <option value="">-- Not linked --</option>
                  {urbanrideAccounts.production.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} (ID: {account.id})
                    </option>
                  ))}
                </select>
                {urbanrideAccounts.production.length === 0 && !accountsLoading.production && (
                  <p className="mt-1 text-xs text-ui-amber-fg">
                    No accounts available. Check Bot API configuration.
                  </p>
                )}
              </div>

              {/* Auto-Provision Toggle */}
              <div className="flex items-center justify-between gap-4 p-3 bg-ui-sunk border border-ui-divider rounded-xl">
                <div>
                  <h4 id="auto-provision-label" className="font-semibold text-ui-ink text-sm">Auto-Provision API Keys</h4>
                  <p className="text-xs text-ui-muted">Automatically create keys for users</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingOrg.auto_provision_enabled}
                    onChange={(e) => setEditingOrg({ ...editingOrg, auto_provision_enabled: e.target.checked })}
                    className="sr-only peer"
                    aria-labelledby="auto-provision-label"
                  />
                  <div className="w-11 h-6 bg-ui-control peer-focus:ring-4 peer-focus:ring-ui-primary/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-ui-on-primary after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow peer-checked:bg-ui-primary"></div>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-ui-divider flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingOrg(null)}
                className={`${btnSecondary} h-10 px-4`}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={btnPrimary}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-ui-on-primary/40 border-t-ui-on-primary" aria-hidden="true"></div>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
